import React, { useRef, useState } from 'react';

import { TextField, TextFieldProps } from '@mui/material';

type KatakanaTextFieldProps = {
  /** 現在の値 */
  value: string;
  /** 値変更ハンドラ（全角カタカナのみの文字列を返す） */
  onChange: (value: string) => void;
} & Omit<TextFieldProps, 'value' | 'onChange'>;

/**
 * 全角カタカナのみ入力可能な TextField。
 *
 * - IME確定後に全角カタカナ以外の文字を除去する。
 * - 全角ひらがなは自動的に全角カタカナに変換する。
 * - 長音符（ー）、中点（・）、全角スペース（　）は許可する。
 */
const KatakanaTextField: React.FC<KatakanaTextFieldProps> = ({ value, onChange, ...rest }) => {
  const [compositionValue, setCompositionValue] = useState<string | null>(null);
  const isComposingRef = useRef(false);

  /**
   * 全角ひらがなを全角カタカナに変換する。
   */
  const hiraganaToKatakana = (str: string): string => {
    return str.replace(/[\u3041-\u3096]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60));
  };

  /**
   * 許可文字のみにフィルタリングする。
   * 許可: 全角カタカナ (ァ-ヶ)、長音符 (ー)、中点 (・)、全角スペース (　)
   */
  const filterKatakana = (str: string): string => {
    return str.replace(/[^\u30A1-\u30F6\u30FC\u30FB\u3000]/g, '');
  };

  /**
   * 入力値を正規化する: ひらがな→カタカナ変換後、許可文字のみ残す。
   */
  const normalize = (str: string): string => {
    return filterKatakana(hiraganaToKatakana(str));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (isComposingRef.current) {
      // IME入力中はローカル表示用の値を更新（ユーザーに入力中の文字を見せる）
      setCompositionValue(rawValue);
      return;
    }
    // IME外（直接入力）の場合は即座に正規化
    const normalized = normalize(rawValue);
    onChange(normalized);
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = false;
    setCompositionValue(null);
    // IME確定後に正規化する
    const normalized = normalize((e.target as HTMLInputElement).value);
    onChange(normalized);
  };

  const { sx, ...textFieldProps } = rest;

  return (
    <TextField
      {...textFieldProps}
      value={compositionValue !== null ? compositionValue : value}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, ...sx }}
    />
  );
};

export default KatakanaTextField;
